import type { BulkEditMeta } from '../TanTable';
import type { TablemaintField } from '../../config/dataTables.registry';
import type { AddableColumn } from '../../services/tablemaint.service';
import type { Option } from './fields';
import type { FieldOptionsMap } from './useFieldOptions';

/**
 * Which grid columns may be edited — in place, or across a dragged range — and with
 * what widget.
 *
 * The BACKEND decides what is writable at all (`AddableColumn.mass_edit`): the
 * structural rules, which are correctness rather than taste — not editable,
 * create-only, the soft-delete flag, a multiselect's backing column, binary. It used
 * to refuse free text too; that came out once edits became staged, since the user
 * now reads a count of amber cells before pressing Save.
 *
 * This picks the widget, using the two things only the registry knows:
 *
 *   OPTION LISTS. A column the drawer offers as a dropdown should be a dropdown here
 *   too, whether its options are a literal list (movetype, equiptype, size) or come
 *   from an endpoint (scac, via /cms-lookups/scacs). The backend sees only `varchar`
 *   for all of these, so without the registry they would land as free-text boxes and
 *   invite typos into coded columns.
 *
 *   ASYNC FK PICKERS. `type: 'lookup'` searches the server per keystroke over sets
 *   too large to enumerate — carriers alone is ~23,700 rows. There is no dropdown
 *   that expresses it, so those columns stay drawer-only.
 *
 * Anything this returns null for simply has no editing affordance on the cell — no
 * disabled state to explain, because a column the user cannot edit here is not a
 * column they were told about.
 */

/** A flag's only two values. The backend coerces to these regardless. */
const YN_OPTIONS = [
  { value: 'Y', label: 'Yes' },
  { value: 'N', label: 'No' },
];

/** An async FK picker — searched per keystroke over a set too large to enumerate,
 *  so there is no dropdown that expresses it. */
const isAsyncLookup = (f: TablemaintField): boolean => f.type === 'lookup' || !!f.lookupTarget;

/** Not a single scalar the user may retype in a cell.
 *
 *  `editable: false` means two different things depending on the table, which is why
 *  it is only consulted for one of them. On a GENERIC-drawer table it is a deliberate
 *  restriction — "shown in the table only" — and mass edit must respect it. On a
 *  table with a BESPOKE drawer the registry fields describe the grid and nothing
 *  else, so every one of them is marked `editable: false` while the drawer edits them
 *  perfectly well (SCACs marks `scac` and `name` that way, and the engine lists both
 *  as writable). Honouring the flag there would silently lock the very columns those
 *  tables exist to maintain, so the backend's own `editable` — already checked by the
 *  caller — is the authority instead. */
const isUneditableShape = (f: TablemaintField, hasCustomDrawer: boolean): boolean =>
  f.type === 'multiselect' || !!f.createOnly || (!hasCustomDrawer && f.editable === false);

/** A joined FK with no option list to pick from — the cell would offer a bare recno
 *  box, which is a footgun. With an option list it is a normal dropdown. */
const isBareForeignKey = (f: TablemaintField, options: Option[]): boolean =>
  options.length === 0 && (!!f.idKey || !!f.refNameKey);

/** The option list for a field: its literal `options`, or whatever its
 *  `optionsEndpoint` returned (already fetched by the page for the drawer). */
const optionsFor = (f: TablemaintField, fetched: FieldOptionsMap): Option[] => {
  const raw = f.optionsFromRows ? fetched[f.key] ?? []
    : f.optionsEndpoint ? fetched[f.optionsEndpoint] ?? [] : f.options ?? [];
  return raw.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
};

/**
 * The bulk-edit widget for one grid column, or null when it may not be mass-edited.
 *
 * @param field  the grid's field spec — synthesized for a picker-added column
 * @param meta   what GET /tablemaint/{table}/columns said about it (absent for a
 *               column the backend does not publish, which then gets no affordance)
 * @param fetchedOptions  endpoint-backed option lists the page has already loaded
 * @param hasCustomDrawer  whether this table has a bespoke drawer — changes what the
 *               registry's `editable: false` means (see isUneditableShape)
 */
export function bulkEditMetaFor(
  field: TablemaintField,
  meta: AddableColumn | undefined,
  fetchedOptions: FieldOptionsMap = {},
  hasCustomDrawer = false,
): BulkEditMeta | null {
  if (!meta || !meta.mass_edit || !meta.editable) return null;
  if (isAsyncLookup(field) || isUneditableShape(field, hasCustomDrawer)) return null;

  // An option list wins over whatever the DB type suggests: these columns are
  // `varchar` underneath, and a free-text box would invite typos into a coded value.
  const options = optionsFor(field, fetchedOptions);
  if (options.length) return { type: 'dropdown', options };
  if (isBareForeignKey(field, options)) return null;

  switch (meta.mass_edit) {
    case 'yn':
      return { type: 'dropdown', options: YN_OPTIONS };
    case 'number':
      return { type: 'number' };
    case 'date':
      return { type: 'date' };
    case 'select':
      // A DB enum whose values the backend knows are constrained but not to what,
      // and the registry offered no list — nothing to put in the dropdown.
      return null;
    case 'text':
      return { type: 'text' };
    default:
      return null;
  }
}

/** Human summary of a mass edit, for the toast. Reports `unchanged` separately so a
 *  batch that wrote nothing doesn't read as a success. */
export function massEditSummary(
  updated: number,
  unchanged: number,
  failed: number,
  noun: string,
): string {
  const parts = [`${updated} ${updated === 1 ? noun : `${noun}s`} updated`];
  if (unchanged) parts.push(`${unchanged} already set`);
  if (failed) parts.push(`${failed} failed`);
  return parts.join(' · ');
}
